<template>
  <div class="row">
    <div class="col-12">
      <card class="custom-shadow-card-header">
        <div class="row">
          <div class="col-12 col-sm-6">
            <label>{{ $t('common_words.farm') }} *</label>
            <br />
            <el-select
              clearable
              @change="hideView"
              filterable
              class="select-primary custom-width-select shadow-sm farm-form"
              size="large"
              v-model="groundForm.farm_id"
              :placeholder="`${$t('common_words.select')}...`"
              v-validate="modelValidations.farm_id"
            >
              <el-option
                v-for="(option, index) in farms"
                class="select-primary"
                :value="option.id"
                :label="option.name"
                :key="index + option.id"
              ></el-option>
            </el-select>
          </div>
          <div class="col-12 col-sm-6">
            <label>{{ $t('common_words.date') }}</label>
            <el-date-picker
              v-if="renderComponent"
              @change="hideView"
              :picker-options="pickerOptions"
              class="shadow-sm date-form"
              v-model="groundForm.date"
              type="date"
              value-format="yyyy-MM-dd"
              range-separator="-"
              :placeholder="`${$t('common_words.select2')}...`"
            >
            </el-date-picker>
          </div>
          <div class="col-12 col-md-6">
            <label class="label-primary" style="color: transparent">{{
              $t('common_words.generate')
            }}</label
            ><br />
            <button
              style="margin-right: 10px"
              class="btn btn-info btn-fg"
              @click.prevent="generate"
            >
              <i class="fa fa-list-alt"></i> {{ $t('common_words.generate') }}
            </button>
            <button class="btn btn-info btn-fg" @click.prevent="preload">
              <i class="fa fa-table"></i> {{ $t('common_words.preload') }}
            </button>
          </div>
        </div>
        <br />
      </card>
      <card class="custom-shadow-card-header" v-if="showPreload">
        <form>
          <div class="row">
            <div class="col-12">
              <div class="text-center">
                <div class="form-group has-label">
                  <el-upload
                    drag
                    ref="upload"
                    :action="getAxiosURL() + '/auth/grounds/grounds/preload'"
                    :data="{ farm_id: groundForm.farm_id, date: formatDate }"
                    :headers="headerInfo"
                    :limit="1"
                    :on-exceed="handleExceed"
                    :on-success="handleSuccess"
                    :before-upload="beforeUpload"
                    :file-list="fileList"
                    :auto-upload="false"
                  >
                    <i class="el-icon-upload"></i>
                    <div class="el-upload__text">
                      {{ $t('common_words.finished_format') }}
                      <em>{{ $t('common_words.click_to_load') }}</em>
                    </div>
                    <div slot="tip" class="el-upload__tip">
                      {{ $t('common_words.xlsx_notice') }}
                    </div>
                  </el-upload>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="text-center" v-if="isCreate()">
                <button
                  style="margin-right: 10px"
                  class="btn btn-info btn-fg"
                  @click.prevent="downloadFormat"
                >
                  <i class="fa fa-file-excel-o"></i>
                  {{ $t('common_words.download_file') }}
                </button>
                <button
                  class="btn btn-info btn-fg"
                  @click.prevent="submitUpload"
                >
                  <i class="fa fa-plus"></i> {{ $t('common_words.load_file') }}
                </button>
              </div>
            </div>
          </div>
        </form>
        <br />
      </card>
      <card
        class="custom-shadow-card-header custom-table-responsive"
        v-if="showTable"
      >
        <div class="mt-4">
          <div class="row" v-if="isIndex() && (isCreate() || isUpdate())">
            <div class="table-responsive">
              <table class="table table-striped table-bordered not-last-td">
                <thead>
                  <tr class="cusmtom-thead-color">
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.allotment') }}
                    </th>
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.depth') }}
                      (cm)
                    </th>
                    <th
                      scope="col"
                      v-for="(row, i) in groundForm.nutrientColumns"
                      class="text-center p-2 custom-header-label"
                      :key="i"
                    >
                      {{ row.label }} <br />
                      <span class="custom-unit-label" v-if="row.unit">{{
                        row.unit
                      }}</span>
                    </th>
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.variety') }}
                    </th>
                    <th
                      scope="col"
                      class="text-center p-3 custom-header-label not-word"
                    >
                      {{ $t('common_words.net_area') }} <br />
                      (ha)
                    </th>
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.population') }} <br />
                      {{ $t('common_words.plants_ha') }}
                    </th>
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.average_weight') }}<br />
                      (Kg)
                    </th>
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.shrinkage') }} <br />
                      (%)
                    </th>
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.return') }}
                    </th>
                    <th scope="col" class="text-center p-3 custom-header-label">
                      {{ $t('common_words.bulk_density') }}
                    </th>
                    <th
                      scope="col"
                      class="text-center p-3 custom-header-label not-word"
                    >
                      {{ $t('production.potential_boxes_x_ha') }}
                    </th>
                    <th
                      scope="col"
                      class="text-center p-3 custom-header-label sticky-col-th"
                    >
                      {{ $t('common_words.operations') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in groundForm.nGround" :key="i">
                    <td class="text-center" style="min-width: 200px">
                      <el-select
                        clearable
                        @clear="clearLotSelect(i)"
                        filterable
                        style="width: 70%; margin: auto"
                        class="select-primary custom-width-select required-lots shadow-sm"
                        size="large"
                        v-model="groundForm.lots_sel[i]"
                        @change="changeArray(i)"
                        :placeholder="`${$t('common_words.select')}...`"
                      >
                        <el-option
                          v-for="l in availableLots"
                          class="select-primary"
                          :value="l.id"
                          :label="l.name"
                          :key="l.id"
                        ></el-option>
                      </el-select>
                    </td>
                    <td class="text-center">
                      <el-select
                        clearable
                        v-model="groundForm.depth[i]"
                        size="large"
                        class="select-primary custom-width-select shadow-sm"
                        :placeholder="`${$t('common_words.select')}...`"
                        style="min-width: 135px"
                        :disabled="disables[i]"
                      >
                        <el-option
                          v-for="option in getAvailableDepthsForLot(
                            groundForm.lots_sel[i],
                            i
                          )"
                          :key="option"
                          :label="option"
                          :value="option"
                        ></el-option>
                      </el-select>
                    </td>
                    <td
                      v-for="(column, index) in groundForm.nutrientColumns"
                      class="text-center"
                      :key="index"
                    >
                      <el-input
                        type="number"
                        min="1"
                        max="9999"
                        style="width: 100px"
                        class="shadow-sm text-center required-inputs"
                        :class="
                          column.type == 'Textura' ? 'required-textures' : ''
                        "
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==5) return false;"
                        :disabled="disables[i]"
                        v-model="groundForm.inputs[i + '-' + column.prop]"
                      />
                    </td>
                    <td class="text-center" style="min-width: 200px">
                      <el-select
                        clearable
                        filterable
                        style="width: 100%; margin: auto"
                        class="select-primary custom-width-select shadow-sm"
                        size="large"
                        v-model="groundForm.clone_sel[i]"
                        :disabled="disables[i]"
                        :placeholder="`${$t('common_words.select')}...`"
                      >
                        <el-option
                          v-for="l in crop_varieties"
                          class="select-primary"
                          :value="l.id"
                          :label="l.name"
                          :key="l.id"
                        ></el-option>
                      </el-select>
                    </td>
                    <td class="text-center">
                      <el-input
                        type="number"
                        min="0"
                        max="9999"
                        style="width: 100px"
                        class="shadow-sm text-center"
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==5) return false;"
                        :disabled="disables[i]"
                        v-model="groundForm.net_areas[i]"
                      />
                    </td>
                    <td class="text-center">
                      <el-input
                        type="number"
                        min="0"
                        max="9999"
                        style="width: 110px"
                        class="shadow-sm text-center"
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==4) return false;"
                        :disabled="disables[i]"
                        v-model="groundForm.populations[i]"
                      />
                    </td>
                    <td class="text-center">
                      <el-input
                        type="number"
                        min="0"
                        max="9999"
                        style="width: 100px"
                        class="shadow-sm text-center"
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==5) return false;"
                        :disabled="disables[i]"
                        v-model="groundForm.average_weights[i]"
                      />
                    </td>
                    <td class="text-center">
                      <el-input
                        type="number"
                        min="0"
                        max="9999"
                        style="width: 100px"
                        class="shadow-sm text-center"
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==4) return false;"
                        :disabled="disables[i]"
                        v-model="groundForm.losses[i]"
                      />
                    </td>
                    <td class="text-center">
                      <el-input
                        type="number"
                        min="0"
                        max="9999"
                        style="width: 100px"
                        class="shadow-sm text-center"
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==5) return false;"
                        :disabled="disables[i]"
                        v-model="groundForm.returns[i]"
                      />
                    </td>
                    <td class="text-center">
                      <el-input
                        type="number"
                        min="0"
                        max="9999"
                        style="width: 100px"
                        class="shadow-sm text-center"
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==5) return false;"
                        :disabled="disables[i]"
                        v-model="groundForm.apparent_densities[i]"
                      />
                    </td>
                    <td class="text-center">
                      <el-input
                        read-only
                        type="number"
                        min="0"
                        max="9999"
                        style="width: 120px"
                        class="shadow-sm text-center"
                        pattern="/^-?\d+\.?\d*$/"
                        onKeyPress="if(this.value.length==4) return false;"
                        :disabled="disables[i]"
                        :value="
                          parseFloat(
                            (groundForm.populations[i] *
                              0.96 *
                              (groundForm.average_weights[i] *
                                groundForm.returns[i]) *
                              ((100 - groundForm.losses[i]) / 100)) /
                              18.86
                          ).toFixed(2)
                        "
                      />
                    </td>
                    <td
                      class="text-center sticky-col-td"
                      style="display: table-cell; min-width: 130px"
                    >
                      <div class="d-flex justify-content-center mt-3">
                        <a
                          v-if="isUpdate() && groundForm.nGround < lots.length"
                          v-tooltip.top-center="$t('common_words.add')"
                          class="btn-warning btn-simple btn-link custom-margin-actions"
                          @click="addRowTable(i)"
                        >
                          <em class="fa fa-plus custom-size-fa-actions"></em>
                        </a>
                        <a
                          v-if="groundForm.nGround > 1 && isDelete()"
                          v-tooltip.top-center="$t('common_words.erase')"
                          class="btn-danger btn-simple btn-link custom-margin-actions"
                          @click="handleDelete(groundForm.lots_sel[i], i)"
                        >
                          <em class="fa fa-times custom-size-fa-actions"></em>
                        </a>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="col-12 mt-5">
              <div class="row">
                <div class="col-6 text-right">
                  <button
                    class="btn btn-fill btn-info btn-wd shadow-sm"
                    @click="validateForm"
                  >
                    {{ $t('common_words.save_data') }}
                  </button>
                </div>
                <div class="col-6">
                  <button
                    class="btn btn-fill btn-danger btn-wd shadow-sm"
                    @click="DeleteAll"
                  >
                    {{ $t('common_words.delete') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <br />
            <h5 class="text-muted text-center">
              {{ $t('common_words.no_permissions') }}
            </h5>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import mixinMasters from 'src/util/mixinMasters';
import MixinPermissions from 'src/util/mixinPermissions';
import { MixinExport } from 'src/mixins/mixin-export';
import SweetAlert from 'src/class/Alerts';
import moment from 'moment';
import { MixinNotification } from 'src/mixins/mixin-notification.js';
import { Axios } from 'src/class/Axios.js';
import MixinMenu from 'src/util/mixinMenu';
import i18n from 'src/i18n';

export default {
  data() {
    return {
      groundForm: {
        farm_id: null,
        ringing_reason_id: null,
        date: moment().format('YYYY-MM-DD'),
        nGround: 1,
        lots_sel: [],
        clone_sel: [],
        net_areas: [],
        populations: [],
        average_weights: [],
        losses: [],
        returns: [],
        apparent_densities: [],
        inputs: {},
        inputs_bck: {},
        depth_bck: {},
        depth: [],
        user_id: this.$store.state.auth.user.id,
      },
      old_lots_sel: [],
      totals: {},
      tableWeekColumns: [],
      showTable: false,
      showPreload: false,
      farms: null,
      disables: [],
      lots: null,
      firstColumn: null,
      secondColum: null,
      firstElement: null,
      crop_varieties: [],
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now();
        },
      },
      headerInfo: {
        Authorization: 'Bearer ' + this.$store.state.auth.token,
      },
      fileList: [],
      modelValidations: {
        farm_id: {
          required: true,
        },
      },
      depthOptions: ['0-20', '20-40', '40-60'],
    };
  },

  computed: {
    availableLots() {
      return this.lots;
    },

    formatDate() {
      return moment(this.groundForm.date).format('YYYY-MM-DD');
    },
  },

  mixins: [
    mixinMasters,
    MixinPermissions,
    MixinNotification,
    MixinExport,
    MixinMenu,
  ],
  watch: {
    '$i18n.locale': 'forceRerender',
  },

  methods: {
    getAvailableDepthsForLot(lotId, currentIndex) {
      const count = 0;

      return this.depthOptions.filter((depth) => {
        for (let i = 0; i < this.groundForm.lots_sel.length; i++) {
          if (
            i !== currentIndex &&
            this.groundForm.lots_sel[i] === lotId &&
            this.groundForm.depth[i] === depth
          ) {
            return false;
          }
        }

        return true;
      });
    },

    clearLotSelect(index) {
      this.groundForm.lots_sel[index] = null;
      this.disables[index] = true;
      this.groundForm.depth[index] = null;
      this.groundForm.clone_sel[index] = null;
      this.groundForm.net_areas[index] = null;
      this.groundForm.populations[index] = null;
      this.groundForm.average_weights[index] = null;
      this.groundForm.losses[index] = null;
      this.groundForm.returns[index] = null;
      this.groundForm.apparent_densities[index] = null;

      Object.keys(this.groundForm.inputs).forEach((key) => {
        let split = key.split('-');
        if (parseInt(split[0]) === index) {
          delete this.groundForm.inputs[key];
        }
      });
    },

    changeArray(index) {
      if (typeof this.old_lots_sel[index] !== 'undefined') {
        for (const key in this.groundForm.inputs) {
          let split = key.split('-');
          if (split[0] == this.old_lots_sel[index]) {
            this.$set(
              this.groundForm.inputs,
              this.groundForm.lots_sel[index] + '-' + split[1],
              this.groundForm.inputs[key]
            );
            delete this.groundForm.inputs[split[0] + '-' + split[1]];
          }
        }
        delete this.totals[this.old_lots_sel[index]];
      }

      if (
        this.groundForm.lots_sel[index] != null &&
        this.groundForm.lots_sel[index] != null
      ) {
        this.disables[index] = false;
      }

      const selectedDepth = this.groundForm.depth[index];
      const selectedLot = this.groundForm.lots_sel[index];
      if (selectedDepth) {
        const duplicate = this.groundForm.lots_sel.some(
          (lot, i) =>
            i !== index &&
            lot === selectedLot &&
            this.groundForm.depth[i] === selectedDepth
        );
        if (duplicate) {
          this.groundForm.depth[index] = null;
        }
      }

      this.old_lots_sel = Object.assign([], this.groundForm.lots_sel);
    },

    addRowTable(index) {
      this.groundForm.nGround++;
      this.disables[this.groundForm.nGround - 1] = true;
      this.disables['undefined'] = true;
    },

    hideView() {
      this.showTable = false;
      this.showPreload = false;
    },

    cleanView() {
      this.groundForm.lots_sel = [];
      this.groundForm.clone_sel = [];
      this.groundForm.net_areas = [];
      this.groundForm.populations = [];
      this.groundForm.average_weights = [];
      this.groundForm.losses = [];
      this.groundForm.returns = [];
      this.groundForm.apparent_densities = [];
      this.groundForm.inputs = {};
      this.groundForm.inputs_bck = {};
      this.groundForm.inputs_ids = {};
      this.groundForm.depth_bck = {};
      this.groundForm.depth = [];
      this.tableWeekColumns = [];
      this.disables = [];
      this.totals = {};
    },

    validateFilter() {
      this.groundForm.id = null;

      if (this.groundForm.farm_id == null) {
        this.highlightItem('farm-form');

        return false;
      } else if (this.groundForm.date == null) {
        this.highlightItem('date-form');

        return false;
      } else {
        return true;
      }
    },

    generate() {
      this.cleanView();
      this.groundForm.id = null;

      if (this.validateFilter()) {
        this.showHideLoading(true);
        this.groundForm.route = 'grounds/grounds';
        //this.groundForm.date = moment(this.groundForm.date).format("YYYY-MM-DD"),

        Axios.getMasters('lots/' + this.groundForm.farm_id)
          .then((r1) => {
            Axios.saveMaster(this.groundForm)
              .then((r2) => {
                this.lots = r1.data.data;
                this.groundForm.nGround = r2.data.nGround;
                this.groundForm.nutrientColumns = r2.data.nutrientColumns;

                if (typeof r2.data.id !== 'undefined') {
                  this.groundForm.id = r2.data.id;
                } else {
                  this.disables[0] = true;
                }

                if (r2.data.lots_sel.length > 0) {
                  this.groundForm.lots_sel = r2.data.lots_sel;
                  this.groundForm.lots_sel_bck = Object.assign(
                    [],
                    this.groundForm.lots_sel
                  );
                  this.old_lots_sel = Object.assign(
                    [],
                    this.groundForm.lots_sel
                  );

                  try {
                    this.groundForm.lots_sel = [];
                    setTimeout(() => {
                      this.groundForm.lots_sel = Object.assign(
                        [],
                        this.groundForm.lots_sel_bck
                      );
                    }, 5);
                  } catch (error) {}

                  this.groundForm.clone_sel = r2.data.clone_sel;
                  this.groundForm.net_areas = r2.data.net_areas;
                  this.groundForm.populations = r2.data.populations;
                  this.groundForm.average_weights = r2.data.average_weights;
                  this.groundForm.losses = r2.data.losses;
                  this.groundForm.returns = r2.data.returns;
                  this.groundForm.depth = r2.data.depth;
                  this.groundForm.depth_bck = Object.assign(
                    [],
                    this.groundForm.depth
                  );
                  this.groundForm.apparent_densities =
                    r2.data.apparent_densities;
                } else {
                  this.disables['undefined'] = true;
                }

                if (Object.keys(r2.data.inputs).length) {
                  this.groundForm.inputs = r2.data.inputs;
                  this.groundForm.inputs_bck = Object.assign(
                    {},
                    this.groundForm.inputs
                  );
                  this.groundForm.inputs_ids = r2.data.inputs_ids;
                }

                this.crop_varieties = r2.data.cropVarieties;
                this.showTable = true;
                this.showPreload = false;
                this.showHideLoading();
              })
              .catch((error) => console.log(error));
          })
          .catch((error) => console.log(error));
      }
    },

    preload() {
      this.cleanView();
      this.groundForm.id = null;

      if (this.validateFilter()) {
        this.showTable = false;
        this.showPreload = true;
      }
    },

    handleDelete(index, i) {
      try {
        this.groundForm.lots_sel.splice(i, 1);
        this.groundForm.clone_sel.splice(i, 1);
        this.groundForm.net_areas.splice(i, 1);
        this.groundForm.populations.splice(i, 1);
        this.groundForm.average_weights.splice(i, 1);
        this.groundForm.losses.splice(i, 1);
        this.groundForm.returns.splice(i, 1);
        this.groundForm.apparent_densities.splice(i, 1);
        this.groundForm.depth.splice(i, 1);
        this.disables.splice(i, 1);

        for (const key in this.groundForm.inputs) {
          let split = key.split('-');
          if (parseInt(split[0]) === i) {
            delete this.groundForm.inputs[key];
          }
        }

        const newInputs = {};
        for (const key in this.groundForm.inputs) {
          let split = key.split('-');
          let rowIndex = parseInt(split[0]);
          if (rowIndex > i) {
            const newKey = rowIndex - 1 + '-' + split[1];
            newInputs[newKey] = this.groundForm.inputs[key];
          } else {
            newInputs[key] = this.groundForm.inputs[key];
          }
        }
        this.groundForm.inputs = newInputs;
      } catch (error) {}

      this.groundForm.nGround -= 1;
    },

    validateForm() {
      let passed = true;
      let amount_texture = 0;

      for (const key in this.groundForm.inputs) {
        const value = this.groundForm.inputs[key];
        if (value !== '' && value !== null && Number(value) <= 0) {
          SweetAlert.warning(
            i18n.t('sweet_alert.ground.no_negative_or_zero_nutrients')
          );

          return;
        }
      }

      for (let i = 0; i < this.groundForm.nGround; i++) {
        let count = 0;
        for (const key in this.groundForm.inputs) {
          let split = key.split('-');
          if (parseInt(split[0]) === i) {
            let value = this.groundForm.inputs[key];
            let columnProp = Number(split[1]);
            let index_nutrient = this.groundForm.nutrientColumns.findIndex(
              (column) => column.prop === columnProp
            );

            if (value != '' && value != null) {
              if (
                this.groundForm.nutrientColumns[index_nutrient] &&
                this.groundForm.nutrientColumns[index_nutrient].type ==
                  'Textura'
              ) {
                amount_texture += parseFloat(value);
              }
              count++;
            }
            if (value != '' && value != null && parseFloat(value) == 0) {
              count = 0;
              break;
            }
          }
        }
        for (const key in this.groundForm.lots_sel) {
          if (this.groundForm.depth[i]) {
            if (this.groundForm.depth[i] == null) {
              countTwo += 1;
              if (countTwo >= 2) {
                count++;
              }
            }
          }
        }

        let lotNullDepthCount = {};
        let error = false;
        this.groundForm.lots_sel.forEach((lotId, idx) => {
          let depth = this.groundForm.depth[idx];
          if (lotId && (!depth || depth === '')) {
            if (!lotNullDepthCount[lotId]) lotNullDepthCount[lotId] = 0;
            lotNullDepthCount[lotId]++;
            if (lotNullDepthCount[lotId] > 1) {
              error = true;
            }
          }
        });

        if (error) {
          SweetAlert.warning(
            i18n.t('sweet_alert.ground.duplicate_lot_with_empty_depth')
          );

          return;
        }

        if (count == 0) {
          passed = false;
        }
      }

      if (this.groundForm.nGround != this.groundForm.lots_sel.length) {
        return this.validationInputsRequired('lots');
      } else if (
        passed == false ||
        Object.keys(this.groundForm.inputs).length == 0
      ) {
        SweetAlert.warning(i18n.t('sweet_alert.ground.capture_floor_warning'));
      } else if (amount_texture > 100) {
        return (
          this.validationInputsRequired('textures'),
          SweetAlert.warning(i18n.t('sweet_alert.ground.average_total_warning'))
        );
      } else {
        if (!this.validateArrayField('lots_sel')) {
          return this.validationInputsRequired('lots', true);
        } else {
          this.storeUpdateGround();
        }
      }
    },

    validateArrayField(field) {
      let passed = true;

      this.groundForm[field].forEach((elem) => {
        if (
          elem == null ||
          elem == '' ||
          (elem != '' && elem != null && parseFloat(elem) == 0) ||
          typeof elem == 'undefined'
        ) {
          passed = false;
        }
      });

      return passed;
    },

    storeUpdateGround() {
      this.showHideLoading(true);

      if (this.isCreate() || this.isUpdate()) {
        this.groundForm.route = 'grounds/grounds/store';
        Axios.saveMaster(this.groundForm)
          .then((response) => {
            this.groundForm.depth_bck = Object.assign(
              [],
              this.groundForm.depth
            );
            this.groundForm.lots_sel_bck = Object.assign(
              [],
              this.groundForm.lots_sel
            );
            this.groundForm.inputs_bck = Object.assign(
              {},
              this.groundForm.inputs
            );
            this.old_lots_sel = Object.assign([], this.groundForm.lots_sel);

            if (typeof response.data.data.id !== 'undefined') {
              this.groundForm.id = response.data.id;
            }

            if (Object.keys(response.data.data.inputs_ids).length) {
              this.groundForm.inputs_ids = response.data.data.inputs_ids;
            }

            this.showHideLoading();
            this.showNotification(
              `${response.data.message}`,
              'success',
              'nc-simple-add',
              i18n.t('common_words.successful_creation_threedots')
            );
            this.showTable = false;
          })
          .catch((error) => this.showMessageErrors(error));
      } else {
        this.showMessageAccess();
        this.showHideLoading();
      }
    },

    DeleteAll() {
      SweetAlert.delete(this.groundForm.date).then((result) => {
        if (result.value) {
          this.showHideLoading(true);
          if (this.groundForm.id && this.isDelete()) {
            this.groundForm.route = 'grounds/grounds';

            Axios.destroyMaster(this.groundForm)
              .then((response) => {
                this.cleanView();
                this.groundForm.nGround = 1;
                this.showHideLoading();
                this.showNotification(
                  `${response.data.message}`,
                  'success',
                  'nc-settings-90',
                  i18n.t('common_words.successful_deletion')
                );
              })
              .catch((error) => this.showMessageErrors(error));
          } else {
            if (!this.isDelete()) {
              this.showMessageAccess();
            } else {
              this.showHideLoading();
            }
          }
        }
      });
    },

    handleExceed(files, fileList) {
      SweetAlert.warning(
        `${i18n.t('sweet_alert.ground.selected_limit')} ${
          files.length
        } ${i18n.t('sweet_alert.ground.files')} `
      );
    },

    handleSuccess(res, file) {
      if (res.errors) {
        let html = ``;
        res.errors.forEach((error) => {
          html += `<p> ${error} </p>`;
        });
        SweetAlert.danger(html);
      } else {
        this.generate();
      }

      this.$refs.upload.clearFiles();
      //this.showHideLoading();
    },

    beforeUpload(file) {
      this.groundForm.date = moment(this.groundForm.date).format('YYYY-MM-DD');
    },

    submitUpload() {
      this.$refs.upload.submit();
    },

    downloadFormat() {
      let self = this;
      let data = self.groundForm;
      data.route = 'grounds/grounds/preformat';
      data.name_file = 'precarga_suelos.xlsx';
      this.Excel(data, 'POST', true)
        .then(() => {
          this.showDownloadNotification();
        })
        .catch((error) => this.showMessageErrors(error));
    },
  },

  created() {
    this.showHideLoading(true);

    if (this.isCreate() || this.isUpdate()) {
      Axios.getMasters('farms/farms/only')
        .then((r) => {
          this.farms = r.data.data;
          this.showHideLoading();
        })
        .catch((error) => this.showMessageErrorsSwalAlert(error));
    } else {
      this.showHideLoading();
    }
  },

  mounted() {
    this.selectedItemOnMenu('grounds');
  },

  destroyed() {
    this.deselectedItemOnMenu('grounds');
  },
};
</script>
<style>
.table-journals td:empty {
  visibility: hidden;
}

.table-journals td:blank {
  visibility: hidden;
}

.custom-unit-label {
  text-transform: none;
}

.sticky-column {
  position: absolute;
  z-index: 100;
  /*height: 100%;*/
  background: #f8fcfe;
  box-shadow: 5px 0px 5px rgba(195, 209, 226, 0.2),
    0px -1px 0px rgba(195, 209, 226, 1);
}

.sticky-col-td {
  position: sticky;
  right: 0;
  background-color: #f8fcfe;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

.sticky-col-th {
  position: sticky;
  right: 0;
  background-color: #888888;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
  min-width: 100px;
}
</style>
